import { KurooApiError } from "@kuroo/sdk";

export function escapeHtml(value: string): string {
  return String(value).replace(/[&<>\"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" })[character] ?? character);
}

export function localDateValue(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function monthValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function monthStart(value: string): Date {
  const [year, month] = value.split("-").map(Number);
  return new Date(year || new Date().getFullYear(), (month || 1) - 1, 1);
}

type ZonedDateTimeFields = { date: string; time: string };

export function zonedDateTimeFields(value: string | Date, timezone: string): ZonedDateTimeFields | null {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    }).formatToParts(date);
    const valueFor = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
    const result = {
      date: `${valueFor("year")}-${valueFor("month")}-${valueFor("day")}`,
      time: `${valueFor("hour")}:${valueFor("minute")}`
    };
    return result.date.includes("-") && !result.date.startsWith("--") && result.time.includes(":") ? result : null;
  } catch {
    return null;
  }
}

export function zonedDateTimeToIso(dateValue: string, timeValue: string, timezone: string): string | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeValue);
  if (!dateMatch || !timeMatch) return null;
  const [, yearValue, monthValue, dayValue] = dateMatch;
  const [, hourValue, minuteValue] = timeMatch;
  const year = Number(yearValue); const month = Number(monthValue); const day = Number(dayValue);
  const hour = Number(hourValue); const minute = Number(minuteValue);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;
  const wall = new Date(0);
  wall.setUTCFullYear(year, month - 1, day);
  wall.setUTCHours(hour, minute, 0, 0);
  if (wall.getUTCFullYear() !== year || wall.getUTCMonth() !== month - 1 || wall.getUTCDate() !== day || wall.getUTCHours() !== hour || wall.getUTCMinutes() !== minute) return null;
  const wallMilliseconds = wall.getTime();
  let candidate = wallMilliseconds;
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const parts = zonedDateTimeFields(new Date(candidate), timezone);
    if (!parts) return null;
    const displayed = zonedDateTimeToWallMilliseconds(parts.date, parts.time);
    if (displayed === null) return null;
    candidate += wallMilliseconds - displayed;
  }
  const resolved = zonedDateTimeFields(new Date(candidate), timezone);
  return resolved?.date === dateValue && resolved.time === timeValue ? new Date(candidate).toISOString() : null;
}

function zonedDateTimeToWallMilliseconds(dateValue: string, timeValue: string): number | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeValue);
  if (!dateMatch || !timeMatch) return null;
  const wall = new Date(0);
  wall.setUTCFullYear(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]));
  wall.setUTCHours(Number(timeMatch[1]), Number(timeMatch[2]), 0, 0);
  return wall.getTime();
}

export function randomIdempotency(prefix: string): string {
  try { return `${prefix}-${crypto.randomUUID()}`; }
  catch { return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`; }
}

export function isAbortError(error: unknown): boolean {
  return error instanceof KurooApiError && error.code === "REQUEST_ABORTED"
    || error instanceof DOMException && error.name === "AbortError"
    || error instanceof Error && (error.name === "AbortError" || /\babort(?:ed|ing)?\b/i.test(error.message));
}
